import React, { useEffect, useState } from 'react';
import { eventsAPI } from '../services/api';
import AlertBox from './AlertBox';

interface EventModalProps {
  uuid: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const initialForm = {
  title: '',
  event_address: '',
  description: '',
  event_ticket_price_adults: '',
  event_ticket_price_kids: '',
  discount_allowed: false,
  event_type: '',
  event_start_date: '',
  event_end_date: '',
};

const EventModal: React.FC<EventModalProps> = ({ uuid, isOpen, onClose, onSave }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && uuid) {
      setLoading(true);
      eventsAPI.getById(uuid)
        .then(res => {
          const event = res.data.event;
          setForm({
            title: event.title || '',
            event_address: event.event_address || '',
            description: event.description || '',
            event_ticket_price_adults: event.event_ticket_price_adults ?? '',
            event_ticket_price_kids: event.event_ticket_price_kids ?? '',
            discount_allowed: !!event.discount_allowed,
            event_type: event.event_type || '',
            event_start_date: event.event_start_date ? event.event_start_date.slice(0, 16) : '',
            event_end_date: event.event_end_date ? event.event_end_date.slice(0, 16) : '',
          });
        })
        .catch(() => setError('Failed to load event'))
        .finally(() => setLoading(false));
    } else {
      setForm(initialForm);
      setError('');
    }
  }, [isOpen, uuid]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let newValue: string | boolean = value;
    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }
    setForm(prev => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSave = () => {
    setLoading(true);
    setError('');
    eventsAPI.update(uuid, form)
      .then(() => {
        onSave();
      })
      .catch(() => setError('Failed to save event'))
      .finally(() => setLoading(false));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md relative">
        <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" onClick={onClose}>&times;</button>
        <h2 className="text-2xl font-bold mb-4">Edit Event</h2>
        {loading ? (
          <div className="text-center py-8 text-primary-600">Loading...</div>
        ) : (
          <form className="space-y-4" onSubmit={e => { e.preventDefault(); handleSave(); }}>
            <div>
              <label className="block text-sm font-semibold mb-1">Title</label>
              <input type="text" name="title" value={form.title} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Address</label>
              <input type="text" name="event_address" value={form.event_address} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Description</label>
              <textarea name="description" value={form.description} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" rows={3} required />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Adults Ticket Price</label>
              <input type="number" name="event_ticket_price_adults" value={form.event_ticket_price_adults} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Kids Ticket Price</label>
              <input type="number" name="event_ticket_price_kids" value={form.event_ticket_price_kids} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" name="discount_allowed" checked={form.discount_allowed} onChange={handleChange} />
              <label className="text-sm">Discount Allowed</label>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Event Type</label>
              <input type="text" name="event_type" value={form.event_type} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Event Start Date</label>
              <input type="datetime-local" name="event_start_date" value={form.event_start_date} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1">Event End Date</label>
              <input type="datetime-local" name="event_end_date" value={form.event_end_date} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            {error && <AlertBox type="error" message={error} />}
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" className="px-4 py-2 rounded-lg bg-gray-200" onClick={onClose}>Cancel</button>
              <button type="submit" className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold">Save</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EventModal;
